import React, { useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import DeveloperNavigation from '../components/DeveloperNavigation';
import { getContractApiUrl } from '../utils/apiUrls';
import { getLoanTimelineApiUrl } from '../utils/apiUrls';

// Loan timeline service URL
const LOAN_TIMELINE_URL = getLoanTimelineApiUrl();

// Backend URL for getting contract info (for seller name)
const BACKEND_URL = getContractApiUrl();

// All loan_timeline columns in order
const COLUMN_ORDER = [
  'id',
  'contract_id',
  'property_address',
  'buyer_name',
  'attachment_file_id',
  'split_attachment_id',
  'email_id_when_created',
  'email_id_most_recent_update',
  'created_at',
  'updated_at',
  'time_reference_date',
  'escrow_closing_date',
  'buyer_initiated_loan_terms_rejection_date',
  'loan_application_deadline',
  'loan_application_status',
  'loan_conditional_approval_deadline',
  'loan_conditional_approval_status',
  'seller_cancellation_right_start_date',
  'seller_cancellation_right_status',
  'property_appraisal_scheduled_date',
  'property_appraisal_received_by_buyer_date',
  'property_appraisal_status',
  'appraised_value_cancellation_deadline',
  'repair_agreement_deadline',
  'final_loan_contingency_notice_deadline'
];

function DeveloperLoans() {
  const { user, isAuthenticated, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();

  const [pageSize, setPageSize] = useState(20); // 10 | 20 | 50
  const [loanTimelines, setLoanTimelines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);

  // Filters
  const [contractId, setContractId] = useState('');
  const [buyerName, setBuyerName] = useState('');
  const [sellerName, setSellerName] = useState('');
  const [dateStart, setDateStart] = useState(''); // ISO date (yyyy-mm-dd)
  const [dateEnd, setDateEnd] = useState('');

  const isKeyFloUser = useMemo(() => {
    const email = user?.email || '';
    const ALLOWED_USERS = ['cole@keyflo.ai', 'james@keyflo.ai', 'flo-test1@keyflo.ai'];
    return ALLOWED_USERS.includes(email);
  }, [user]);

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isKeyFloUser) {
      // Redirect non-KeyFlo users back to home
      navigate('/');
    }
  }, [isAuthenticated, isKeyFloUser, navigate]);

  const formatDateTime = (ts) => {
    try {
      return new Date(ts).toLocaleString();
    } catch (e) {
      return ts || '';
    }
  };

  const formatDate = (dateStr) => {
    try {
      if (!dateStr) return '';
      // If already in YYYY-MM-DD format, return as-is (no timezone conversion)
      if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      // If it's a date-only string in YYYY-MM-DD format, parse components directly to avoid timezone issues
      const dateMatch = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (dateMatch) {
        return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
      }
      // Otherwise, parse and format as YYYY-MM-DD (for other date formats)
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr || '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (e) {
      return dateStr || '';
    }
  };

  const buildParams = (resetOffset = true) => {
    const params = {
      limit: pageSize,
      offset: resetOffset ? 0 : offset
    };

    if (contractId) params.contract_id = contractId;
    if (buyerName) params.buyer_name = buyerName;
    if (sellerName) params.seller_name = sellerName;
    if (dateStart) params.start_date = dateStart;
    if (dateEnd) params.end_date = dateEnd;
    return params;
  };

  const fetchLoanTimelines = async (reset = true) => {
    if (!isAuthenticated || !isKeyFloUser) return;
    try {
      setLoading(true);
      setError('');

      const params = buildParams(reset);
      const response = await axios.get(`${LOAN_TIMELINE_URL}/loan-timelines`, { params });

      if (response.data?.status !== 'success') {
        throw new Error(response.data?.message || 'Failed to fetch loan timelines');
      }

      const newLoanTimelines = response.data.loan_timelines || [];
      setLoanTimelines(reset ? newLoanTimelines : [...loanTimelines, ...newLoanTimelines]);
      setOffset((reset ? 0 : offset) + newLoanTimelines.length);
    } catch (e) {
      setError(e.message || 'Failed to load loan timelines');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => {
    const observed = new Set();
    for (const row of loanTimelines || []) {
      if (!row) continue;
      for (const k of Object.keys(row)) observed.add(k);
    }

    const ordered = [];
    for (const k of COLUMN_ORDER) {
      if (observed.has(k) || COLUMN_ORDER.includes(k)) ordered.push(k);
    }
    for (const k of Array.from(observed).sort()) {
      if (!ordered.includes(k)) ordered.push(k);
    }
    return ordered;
  }, [loanTimelines]);

  const getStickyLeftStyle = (col) => {
    // Freeze only contract_id + property_address on the left
    if (col !== 'contract_id' && col !== 'property_address') return {};

    const contractIdWidth = 140;
    const propertyWidth = 320;

    const left = col === 'contract_id' ? 0 : contractIdWidth;
    const width = col === 'contract_id' ? contractIdWidth : propertyWidth;

    return {
      position: 'sticky',
      left,
      minWidth: width,
      maxWidth: width,
      background: '#ffffff',
      zIndex: 2
    };
  };

  const getStickyHeaderStyle = (col) => {
    const base = {
      textAlign: 'left',
      padding: '10px 12px',
      borderBottom: '1px solid #e5e7eb',
      background: '#f8fafc',
      position: 'sticky',
      top: 0,
      zIndex: 1
    };
    if (col !== 'contract_id' && col !== 'property_address') return base;
    return {
      ...base,
      ...getStickyLeftStyle(col),
      background: '#f8fafc',
      zIndex: 3
    };
  };

  useEffect(() => {
    if (isAuthenticated && isKeyFloUser) fetchLoanTimelines(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isKeyFloUser, pageSize]);

  if (!isAuthenticated) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column' }}>
        <p>Please log in</p>
        <button onClick={() => loginWithRedirect({
          authorizationParams: {
            prompt: 'login'
          }
        })}>Log in</button>
      </div>
    );
  }

  if (!isKeyFloUser) {
    return (
      <div style={{ padding: '24px' }}>
        <h2>Access Denied</h2>
        <p>This page is restricted to KeyFlo team members.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2>Developer • Loan Timelines</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', position: 'relative' }}>
          <button className="back-button" onClick={() => navigate('/')}>← Back to Contracts</button>
          <label style={{ marginRight: '8px' }}>Page size:</label>
          <select value={pageSize} onChange={(e) => setPageSize(parseInt(e.target.value, 10))}>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <DeveloperNavigation />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: '12px', marginBottom: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Contract ID</label>
          <input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="contract_id" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Buyer Name</label>
          <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="buyer_name" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Seller Name</label>
          <input value={sellerName} onChange={(e) => setSellerName(e.target.value)} placeholder="seller_name" style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>Start Date</label>
          <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} style={{ width: '100%' }} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '4px' }}>End Date</label>
          <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} style={{ width: '100%' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button onClick={() => { setOffset(0); fetchLoanTimelines(true); }}>Apply Filters</button>
        <button onClick={() => { setContractId(''); setBuyerName(''); setSellerName(''); setDateStart(''); setDateEnd(''); setOffset(0); fetchLoanTimelines(true); }}>Clear</button>
        <button onClick={() => fetchLoanTimelines(false)}>Load More</button>
      </div>

      {loading && <LoadingSpinner />}
      {error && (
        <div style={{ color: 'red', marginBottom: '12px' }}>{error}</div>
      )}

      <div style={{ overflowX: 'auto', background: '#ffffff', border: '1px solid #e5e7eb' }}>
        <table style={{ width: 'max-content', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  style={getStickyHeaderStyle(col)}
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loanTimelines.map((loanTimeline, idx) => (
              <tr key={loanTimeline.id || idx} style={{ borderBottom: '1px solid #f1f5f9', height: '100px', maxHeight: '100px' }}>
                {columns.map((col) => {
                  let val = loanTimeline[col];
                  if (col === 'created_at' || col === 'updated_at') {
                    val = val ? formatDateTime(val) : '';
                  }
                  if (col.includes('_date') && val) {
                    val = formatDate(val);
                  }
                  if (val === null || typeof val === 'undefined') val = '';
                  return (
                    <td
                      key={col}
                      style={{
                        padding: '10px 12px',
                        borderBottom: '1px solid #f1f5f9',
                        borderRight: '1px solid #f1f5f9',
                        verticalAlign: 'top',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        minWidth: 280,
                        ...getStickyLeftStyle(col)
                      }}
                    >
                      <div style={{ maxHeight: '80px', overflowY: 'auto' }}>
                        {String(val)}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {loanTimelines.length === 0 && !loading && (
              <tr>
                <td colSpan={columns.length} style={{ padding: '12px', textAlign: 'center' }}>No loan timelines found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DeveloperLoans;

